import { isIosDevice } from './device'
import { getCorsFixedUrl, isPresignedUrl } from './dom'

const MIME_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  glb: 'model/gltf-binary',
  gltf: 'model/gltf+json',
  stl: 'model/stl',
}

function getMimeTypeFromFileName(fileName: string): string | undefined {
  const ext = fileName.split('.').pop()?.toLowerCase()
  return ext ? MIME_TYPES[ext] : undefined
}

export type DownloadMediaFileResult =
  | { outcome: 'shared' | 'cancelled' | 'savedToFiles' }
  | { outcome: 'needsUserGesture'; blob: Blob }

export interface DownloadMediaFileOptions {
  url: string
  fileName: string
  /** Inferred from fileName extension when omitted. */
  mimeType?: string
  /**
   * Called with download progress as a percentage (0-100) when the response
   * exposes a `Content-Length` header. When omitted, no progress is tracked
   * and the response body is consumed in one shot.
   */
  onProgress?: (progress: number) => void
  /**
   * When share() throws NotAllowedError after the bytes are in memory, return
   * them instead of falling back to `<a download>`. The caller can share from
   * a later tap that still has a user gesture.
   */
  holdBlobIfShareBlocked?: boolean
}

function isInlineUrl(url: string) {
  return url.startsWith('data:') || url.startsWith('blob:')
}

function getDownloadFetchUrl(url: string) {
  return isInlineUrl(url) ? url : getCorsFixedUrl(url)
}

async function readBodyWithProgress(
  response: Response,
  onProgress?: (progress: number) => void,
): Promise<Blob> {
  const contentLength = response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0
  const contentType = response.headers.get('Content-Type') || ''

  if (!onProgress || !response.body || total <= 0) {
    return await response.blob()
  }

  const reader = response.body.getReader()
  const chunks: BlobPart[] = []
  let received = 0

  onProgress(0)

  for (let result = await reader.read(); !result.done; result = await reader.read()) {
    if (result.value) {
      chunks.push(result.value)
      received += result.value.length
      const progress = Math.min(Math.round((received / total) * 100), 100)
      onProgress(progress)
    }
  }

  onProgress(100)

  return new Blob(chunks, contentType ? { type: contentType } : undefined)
}

function downloadViaAnchor(blobUrl: string, fileName: string) {
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function isShareAbort(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

function isShareBlocked(error: unknown) {
  return error instanceof DOMException && error.name === 'NotAllowedError'
}

function isShareUnsupported(error: unknown) {
  return error instanceof TypeError
}

function createMediaFile(blob: Blob, fileName: string, mimeType?: string) {
  return new File([blob], fileName, { type: mimeType ?? blob.type })
}

/**
 * Probe whether this iOS browser can open the share sheet for a media file.
 * Uses an empty File so callers can skip fetching bytes they cannot share.
 */
export function canShareMediaOnIos(fileName: string, mimeType?: string) {
  if (!isIosDevice() || typeof navigator.share !== 'function' || !navigator.canShare) {
    return false
  }

  const type = mimeType ?? getMimeTypeFromFileName(fileName) ?? ''
  const probe = new File([], fileName, { type })
  return navigator.canShare({ files: [probe] })
}

export async function shareMediaFile({
  blob,
  fileName,
  mimeType,
}: {
  blob: Blob
  fileName: string
  mimeType?: string
}): Promise<'shared' | 'cancelled' | 'blocked'> {
  if (typeof navigator.share !== 'function') {
    return 'blocked'
  }

  try {
    await navigator.share({ files: [createMediaFile(blob, fileName, mimeType)] })
    return 'shared'
  } catch (error) {
    if (isShareAbort(error)) {
      return 'cancelled'
    }
    if (isShareBlocked(error) || isShareUnsupported(error)) {
      return 'blocked'
    }
    throw error
  }
}

export function saveMediaFileViaAnchor({
  blob,
  fileName,
  mimeType,
}: {
  blob: Blob
  fileName: string
  mimeType?: string
}) {
  const blobUrl = URL.createObjectURL(mimeType ? new Blob([blob], { type: mimeType }) : blob)
  downloadViaAnchor(blobUrl, fileName)
  URL.revokeObjectURL(blobUrl)
}

/**
 * Downloads a media file with iOS-aware handling.
 *
 * On iOS, `canShare` is checked before any fetch. If the browser cannot share
 * files, the original URL is saved with `<a download>` so we do not copy the
 * file into memory. If it can, we fetch (without rewriting signed query
 * strings) and call `navigator.share()`.
 *
 * Dismissing the sheet rejects with AbortError and does nothing else.
 * NotAllowedError after a fetch can be returned as `needsUserGesture` so the
 * caller can share from a later tap.
 */
export async function downloadMediaFile({
  url,
  fileName,
  mimeType: explicitMimeType,
  onProgress,
  holdBlobIfShareBlocked = false,
}: DownloadMediaFileOptions): Promise<DownloadMediaFileResult> {
  const mimeType = explicitMimeType ?? getMimeTypeFromFileName(fileName)

  if (isIosDevice()) {
    if (!canShareMediaOnIos(fileName, mimeType)) {
      downloadViaAnchor(url, fileName)
      return { outcome: 'savedToFiles' }
    }
  } else if (!isInlineUrl(url) && isPresignedUrl(url)) {
    downloadViaAnchor(url, fileName)
    return { outcome: 'savedToFiles' }
  }

  const fetchUrl = getDownloadFetchUrl(url)
  const response = await fetch(fetchUrl)

  if (!response.ok) {
    throw new Error(`Failed to fetch: HTTP ${response.status} ${response.statusText}`)
  }

  const blob = await readBodyWithProgress(response, onProgress)
  if (isIosDevice()) {
    const shareOutcome = await shareMediaFile({ blob, fileName, mimeType })
    if (shareOutcome === 'shared') {
      return { outcome: 'shared' }
    }
    if (shareOutcome === 'cancelled') {
      return { outcome: 'cancelled' }
    }
    if (holdBlobIfShareBlocked) {
      return { outcome: 'needsUserGesture', blob }
    }
  }

  saveMediaFileViaAnchor({ blob, fileName, mimeType })
  return { outcome: 'savedToFiles' }
}
